// src/components/AuctionCard.js
import React from 'react';
import './AuctionCard.css';
import Tooltip from './Tooltip';
import {
  Users,
  Coins,
  ShieldCheck,
  ArrowRight,
  Clock3,
  Flag,
  Trash2,
  EyeOff,
  Loader2,
  HatGlasses,
  Lock,
  Layers3,
  Blocks,
  Info,
  ListOrdered,
} from 'lucide-react';
import { DEFAULT_ROSTER_LIMITS, normalizeRosterLimits, getAuctionRolesMode } from '../firebase/auctionService';

function formatRelativeTime(ts) {
  if (!ts) return 'Mai';
  const diff = Date.now() - Number(ts);
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return 'Adesso';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  return `${days} giorni fa`;
}

function getVisibilityLabel(visibility) {
  if (visibility === 'public') return 'Pubblica';
  return 'Privata';
}

function getSourceLabel(source) {
  return source === 'FantApp' ? 'FantApp!' : 'FantaMaster';
}

function getTotalRosterSlots(limits, rolesMode) {
  if (rolesMode === 'classic') {
    return Number(limits.P || 0) + Number(limits.D || 0) + Number(limits.C || 0) + Number(limits.A || 0);
  }

  return Number(limits.P || 0) + Number(limits.M || 0);
}

export default function AuctionCard({ auction, onOpen, onDelete, onOpenAnalysis, deleting = false, boughtPlayersCount = 0, isWatched = false, onToggleVisibility, toggling = false }) {
  const teamsCount = auction.teamNames?.length || auction.participantsCount || 0;
  const limits = normalizeRosterLimits(auction.rosterLimits || DEFAULT_ROSTER_LIMITS);
  const visibilityLabel = getVisibilityLabel(auction.visibility);
  const sourceLabel = getSourceLabel(auction.playersSource);
  const rolesMode = getAuctionRolesMode(auction);
  const isListone = Boolean(auction.listone);
  const totalRosterSlots = getTotalRosterSlots(limits, rolesMode);
  // boughtPlayersCount arriva già come totale di TUTTI gli acquisti dell'asta
  // (tutte le squadre, non solo la propria — vedi AuctionMarketTab), quindi il
  // completamento va confrontato con la capienza di tutti i partecipanti
  // insieme, non con gli slot di una singola squadra.
  const totalRosterSlotsAllTeams = totalRosterSlots * Math.max(1, teamsCount);
  const completionPercent =
    totalRosterSlotsAllTeams > 0
      ? Math.min(100, Math.round((Number(boughtPlayersCount) / totalRosterSlotsAllTeams) * 100))
      : 0;

  return (
    <article className={`auction-card glass${isWatched ? ' auction-card--watched' : ''}`}>
      <div className="auction-card__head">
        <div className="auction-card__heading">
          <div className="auction-card__iconBadge">
            {isWatched ? <HatGlasses size={18} /> : <ShieldCheck size={18} />}
          </div>
          <div className="auction-card__headingText">
            <div className="auction-card__eyebrowRow">
              <span className="auction-card__eyebrow">
                {isWatched ? 'Asta osservata' : 'Asta personale'}
              </span>
              <Tooltip as="span" className="auction-card__updatedAt" content="Ultimo aggiornamento">
                <Clock3 size={11} />
                {formatRelativeTime(auction.updatedAt || auction.createdAt)}
              </Tooltip>
            </div>
            <h3>{auction.name}</h3>
          </div>
        </div>

        <div className="auction-card__headRight">
          <div className="auction-card__actions">
            {onOpenAnalysis && (
              <Tooltip
                as="button"
                className="auction-card__iconBtn"
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenAnalysis(auction.id); }}
                content="Analisi asta"
              >
                <Info size={16} />
              </Tooltip>
            )}

            {!isWatched && onToggleVisibility && (
              <Tooltip
                as="button"
                className="auction-card__iconBtn"
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
                disabled={toggling}
                content={auction.visibility === 'public' ? 'Rendi privata' : 'Rendi pubblica'}
              >
                {toggling ? <Loader2 size={16} className="spin" /> : auction.visibility === 'public' ? <Lock size={16} /> : <HatGlasses size={16} />}
              </Tooltip>
            )}

            {onDelete && (
              <Tooltip
                as="button"
                className="auction-card__iconBtn auction-card__iconBtn--danger"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                disabled={deleting}
                content={isWatched ? 'Smetti di seguire' : 'Elimina asta'}
              >
                {deleting ? <Loader2 size={16} className="spin" /> : isWatched ? <EyeOff size={16} /> : <Trash2 size={16} />}
              </Tooltip>
            )}
          </div>

          <Tooltip
            as="div"
            className={`auction-card__ring${completionPercent >= 100 ? ' auction-card__ring--done' : ''}`}
            style={{ '--pct': completionPercent }}
            role="progressbar"
            aria-valuenow={completionPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Completamento asta"
            content={`${boughtPlayersCount}/${totalRosterSlotsAllTeams} giocatori acquistati`}
          >
            <span>{completionPercent}%</span>
          </Tooltip>
        </div>
      </div>

      <div className="auction-card__tags">
        <span className={`auction-card__tag auction-card__tag--visibility-${auction.visibility === 'public' ? 'public' : 'private'}`}>
          {auction.visibility === 'public' ? <HatGlasses size={12} /> : <Lock size={12} />}
          {visibilityLabel}
        </span>

        <span className="auction-card__tag auction-card__tag--source">
          <Layers3 size={12} />
          {sourceLabel}
        </span>

        <span className={`auction-card__tag auction-card__tag--roles-${rolesMode === 'mantra' ? 'mantra' : 'classic'}`}>
          <Blocks size={12} />
          {rolesMode === 'mantra' ? 'Mantra' : 'Classic'}
        </span>

        {isListone && (
          <span className="auction-card__tag auction-card__tag--listone">
            <ListOrdered size={12} />
            Listone
          </span>
        )}
      </div>

      <div className="auction-card__metaChips">
        {!isListone && (
          <Tooltip as="span" className="auction-card__metaChip" content="Partecipanti">
            <Users size={13} />
            <strong>{teamsCount}</strong> Partecipanti
          </Tooltip>
        )}

        <Tooltip as="span" className="auction-card__metaChip" content="Crediti">
          <Coins size={13} />
          <strong>{auction.credits}</strong> Crediti
        </Tooltip>

        <Tooltip as="span" className="auction-card__metaChip auction-card__metaChip--team" content="La tua squadra">
          <ShieldCheck size={13} />
          Nome squadra personale:
          <strong>{auction.myTeamName || '—'}</strong>
        </Tooltip>
      </div>

      <div className="auction-card__limits">
        <div className="auction-card__limitsHead">
          <Flag size={14} />
          <span>Limiti giocatori</span>
        </div>

        <div className="auction-card__limitsRow">
          <div className="auction-card__limitsPills">
            <span className="auction-card__limitPill auction-card__limitPill--p">
              Portieri: {limits.P}
            </span>

            {rolesMode === 'classic' ? (
              <>
                <span className="auction-card__limitPill auction-card__limitPill--d">
                  Difensori: {limits.D}
                </span>
                <span className="auction-card__limitPill auction-card__limitPill--c">
                  Centrocampisti: {limits.C}
                </span>
                <span className="auction-card__limitPill auction-card__limitPill--a">
                  Attaccanti: {limits.A}
                </span>
              </>
            ) : (
              <span className="auction-card__limitPill auction-card__limitPill--m">
                Giocatori di movimento: {limits.M}
              </span>
            )}
          </div>

          <div className="auction-card__limitsSimple">
            {rolesMode === 'classic'
              ? `P ${limits.P} · D ${limits.D} · C ${limits.C} · A ${limits.A}`
              : `P ${limits.P} · Movimento ${limits.M}`}
          </div>

          <button
            className="auction-card__open"
            type="button"
            onClick={() => onOpen(auction.id)}
          >
            Apri
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </article>
  );
}
